import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate, useNavigate } from "react-router-dom";
import { useEffect } from "react";
import ReactLenis from "lenis/react";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { CartProvider } from "@/context/CartContext";
import { Nav } from "@/components/Nav";
import { Footer } from "@/components/Footer";
import { Cursor } from "@/components/Cursor";
import { ChatWidget } from "@/components/ChatWidget";
import { WhatsAppButton } from "@/components/WhatsAppButton";
import { UnderwaterBg } from "@/components/UnderwaterBg";
import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import Auctions from "@/pages/Auctions";
import Services from "@/pages/Services";
import Wholesale from "@/pages/Wholesale";
import Pricing from "@/pages/Pricing";
import Wysiwyg from "@/pages/Wysiwyg";
import Reviews from "@/pages/Reviews";
import Cart from "@/pages/Cart";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import Policy from "@/pages/Policy";
import Privacy from "@/pages/Privacy";
import Terms from "@/pages/Terms";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

function AdminSubdomain() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  useEffect(() => {
    const host = window.location.hostname;
    if (host.startsWith("admin.") && !pathname.startsWith("/admin")) {
      navigate("/admin/login", { replace: true });
    }
  }, [pathname, navigate]);
  return null;
}

function Protected({ children }) {
  const { user, ready } = useAuth();
  if (!ready) return <div className="min-h-screen flex items-center justify-center text-white/40 font-mono">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;
  return children;
}

function Shell() {
  const { pathname } = useLocation();
  const isAdmin = pathname.startsWith("/admin");
  return (
    <div className="grain min-h-screen">
      {!isAdmin && <UnderwaterBg />}
      <Cursor />
      <Nav />
      <main>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/shop" element={<Shop />} />
          <Route path="/auctions" element={<Auctions />} />
          <Route path="/services" element={<Services />} />
          <Route path="/wholesale" element={<Wholesale />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/wysiwyg" element={<Wysiwyg />} />
          <Route path="/reviews" element={<Reviews />} />
          <Route path="/maintenance" element={<Services />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout/success" element={<CheckoutSuccess />} />
          <Route path="/policy" element={<Policy />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/admin/login" element={<AdminLogin />} />
          <Route path="/admin" element={<Protected><AdminDashboard /></Protected>} />
        </Routes>
      </main>
      {!isAdmin && <Footer />}
      {!isAdmin && <ChatWidget />}
      {!isAdmin && <WhatsAppButton />}
    </div>
  );
}

export default function App() {
  return (
    <ReactLenis root options={{ lerp: 0.09, smoothWheel: true }}>
      <BrowserRouter>
        <AuthProvider>
          <CartProvider>
            <ScrollTop />
            <AdminSubdomain />
            <Shell />
            <Toaster theme="dark" position="bottom-right" richColors />
          </CartProvider>
        </AuthProvider>
      </BrowserRouter>
    </ReactLenis>
  );
}
